import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Phone, Mail, Globe, MessageCircle, ArrowLeft, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import StarRating from "../components/StarRating";
import { useAuth } from "../contexts/AuthContext";
import useSEO from "../hooks/useSEO";

const TYPE_LABEL = { professional: "Professionnel", entrepreneur: "Entrepreneur", organism: "Organisme" };

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useSEO({
    title: profile?.name || "Profil",
    description: profile?.description?.slice(0, 160) || "Découvrez ce profil sur DemkaBiz.",
    ogImage: profile?.photo_url,
  });

  const fetchProfile = () => {
    api.get(`/profiles/${id}`).then((r) => setProfile(r.data)).catch(() => navigate("/"));
  };

  useEffect(() => { fetchProfile(); /* eslint-disable-next-line */ }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate("/connexion"); return; }
    setSubmitting(true);
    try {
      await api.post("/reviews", { profile_id: id, rating, comment });
      setComment("");
      fetchProfile();
      toast.success("Avis publié, merci !");
    } catch (e) {
      toast.error("Erreur lors de la publication");
    } finally { setSubmitting(false); }
  };

  if (!profile) return <div className="max-w-7xl mx-auto p-12 text-center text-muted-foreground">Chargement…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="profile-detail-page">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="card-surface overflow-hidden">
            <div className="aspect-[16/9] sm:aspect-[3/2] bg-secondary">
              {profile.photo_url && <img src={profile.photo_url} alt={profile.name} className="w-full h-full object-cover object-top" />}
            </div>
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="px-3 py-1 rounded-full bg-secondary text-xs font-medium text-primary uppercase tracking-wider">
                  {TYPE_LABEL[profile.profile_type] || profile.profile_type}
                </span>
                {profile.featured && <span className="px-3 py-1 rounded-full bg-primary text-white text-xs font-semibold">En vedette</span>}
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight">{profile.name}</h1>
              <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {profile.city}, {profile.province}</span>
                <StarRating value={profile.avg_rating} count={profile.review_count} size={16} />
              </div>
              <div className="prose prose-sm max-w-none mt-6 text-foreground/90 whitespace-pre-line leading-relaxed">{profile.description}</div>

              {profile.categories?.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-display font-semibold text-sm mb-2 uppercase text-muted-foreground tracking-wider">Catégories</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.categories.map((c) => <span key={c} className="px-3 py-1 rounded-full bg-secondary text-xs font-medium">{c.replace(/-/g, " ")}</span>)}
                  </div>
                </div>
              )}
              {profile.languages?.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-display font-semibold text-sm mb-2 uppercase text-muted-foreground tracking-wider">Langues parlées</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map((l) => <span key={l} className="px-3 py-1 rounded-full border border-emerald-900/10 text-xs">{l}</span>)}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Reviews */}
          <div className="card-surface p-6 sm:p-8 mt-6" data-testid="reviews-section">
            <h2 className="font-display font-bold text-2xl mb-6">Avis ({profile.reviews?.length || 0})</h2>
            {profile.reviews?.length > 0 ? (
              <div className="space-y-5">
                {profile.reviews.map((r) => (
                  <div key={r.id} className="pb-5 border-b border-emerald-900/5 last:border-0" data-testid={`review-${r.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-display font-medium">{r.author_name || "Client"}</div>
                      <StarRating value={r.rating} showCount={false} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.comment}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucun avis pour le moment. Soyez le premier !</p>
            )}

            <form onSubmit={submitReview} className="mt-8 pt-6 border-t border-emerald-900/10" data-testid="review-form">
              <h3 className="font-display font-semibold text-lg mb-3">Laisser un avis</h3>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-muted-foreground">Note :</span>
                {[1, 2, 3, 4, 5].map((i) => (
                  <button key={i} type="button" onClick={() => setRating(i)} data-testid={`rating-star-${i}`}>
                    <svg className={`w-6 h-6 ${i <= rating ? "fill-amber-400 text-amber-400" : "fill-stone-200 text-stone-300"}`} viewBox="0 0 24 24"><path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  </button>
                ))}
              </div>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                required
                placeholder="Partagez votre expérience…"
                className="w-full bg-white border border-emerald-900/10 rounded-xl p-3 text-sm outline-none focus:border-primary"
                data-testid="review-comment"
              />
              <button type="submit" disabled={submitting} className="btn-primary mt-3" data-testid="review-submit">
                <Send className="w-4 h-4" /> Publier
              </button>
              {!user && <p className="text-xs text-muted-foreground mt-2">Vous devez être connecté pour publier un avis.</p>}
            </form>
          </div>
        </div>

        {/* Sidebar - Contact */}
        <div className="lg:col-span-1">
          <div className="card-surface p-6 sticky top-24" data-testid="contact-card">
            <h2 className="font-display font-bold text-xl mb-4">Contacter</h2>
            <div className="space-y-3">
              {profile.whatsapp && (
                <a
                  href={`https://wa.me/${profile.whatsapp.replace(/\D/g, "")}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 transition"
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle className="w-5 h-5 text-[#25D366]" />
                  <div>
                    <div className="text-xs text-muted-foreground">WhatsApp</div>
                    <div className="font-medium text-sm">{profile.whatsapp}</div>
                  </div>
                </a>
              )}
              {profile.phone && (
                <a href={`tel:${profile.phone}`} className="flex items-center gap-3 p-3 rounded-xl bg-secondary hover:bg-secondary/80 transition" data-testid="contact-phone">
                  <Phone className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-muted-foreground">Téléphone</div>
                    <div className="font-medium text-sm">{profile.phone}</div>
                  </div>
                </a>
              )}
              {profile.email && (
                <a href={`mailto:${profile.email}`} className="flex items-center gap-3 p-3 rounded-xl bg-secondary hover:bg-secondary/80 transition" data-testid="contact-email">
                  <Mail className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-muted-foreground">Email</div>
                    <div className="font-medium text-sm break-all">{profile.email}</div>
                  </div>
                </a>
              )}
              {profile.website && (
                <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 rounded-xl bg-secondary hover:bg-secondary/80 transition" data-testid="contact-website">
                  <Globe className="w-5 h-5 text-primary" />
                  <div>
                    <div className="text-xs text-muted-foreground">Site web</div>
                    <div className="font-medium text-sm break-all">{profile.website}</div>
                  </div>
                </a>
              )}
            </div>
            <Link
              to={`/demande?profile_id=${profile.id}`}
              className="btn-primary w-full mt-5 justify-center"
              data-testid="request-service-btn"
            >
              Demander un service
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
